import { fastApi } from '@/services/api';

const PATH = 'v1/customer/documents';

// Mirrors fast-api's user upload sources and per-source file types
export enum DocumentSource {
  COMPANY_DOCUMENTS = 'Company Documents',
  PROOF_OF_FUNDS = 'Proof of Funds',
  LICENCES_AND_POLICIES = 'Licences and Policies',
}

const PDF_AND_IMAGE_FILE_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

export const DOCUMENT_FILE_TYPES_BY_SOURCE: Record<DocumentSource, Record<string, string>> = {
  [DocumentSource.COMPANY_DOCUMENTS]: PDF_AND_IMAGE_FILE_TYPES,
  [DocumentSource.PROOF_OF_FUNDS]: PDF_AND_IMAGE_FILE_TYPES,
  [DocumentSource.LICENCES_AND_POLICIES]: {
    ...PDF_AND_IMAGE_FILE_TYPES,
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
};

export const DOCUMENT_MAX_SIZE_BYTES = 20 * 1024 * 1024;

const DOCUMENT_FILENAME_MAX_LENGTH = 180;

// The local dev server proxies this path to the vault, whose CORS only allows the deployed origins
const DOCUMENT_UPLOAD_LOCAL_PROXY_PATH = '/pii-vault';

export type Document = {
  documentId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string | null;
  source: DocumentSource;
};

type DocumentUploadUrlRequest = {
  filename: string;
  contentType: string;
  sizeBytes: number;
  source: DocumentSource;
};

type DocumentUploadUrlResponse = {
  documentId: string;
  url: string;
  headers: Record<string, string>;
  expiresIn: number;
};

export const getDocumentExtension = (filename: string) => {
  const extensionStart = filename.lastIndexOf('.');

  return extensionStart === -1 ? '' : filename.slice(extensionStart + 1).toLowerCase();
};

// fast-api accepts letters, numbers, spaces and - _ . ( ), up to 180 characters and without '..'
export const toDocumentFilename = (filename: string) => {
  const extension = getDocumentExtension(filename);
  const stem = (extension ? filename.slice(0, -(extension.length + 1)) : filename).replace(/[^\w\-. ()]/g, '_').replace(/\.{2,}/g, '.');
  const suffix = extension ? `.${extension}` : '';
  // A stem left ending in a dot would make '..' with the suffix
  const name = stem.slice(0, DOCUMENT_FILENAME_MAX_LENGTH - suffix.length).replace(/\.+$/, '') || 'document';

  return `${name}${suffix}`;
};

const toDocumentUploadRequestUrl = (url: string) => {
  if (import.meta.env.VITE_APP_ENV !== 'localhost') return url;

  const { pathname, search } = new URL(url);

  return new URL(`${DOCUMENT_UPLOAD_LOCAL_PROXY_PATH}${pathname}${search}`, window.location.origin).toString();
};

export const getDocument = async (documentId: string) => {
  return fastApi.get(`${PATH}/${documentId}`).json<Document>();
};

const postDocumentUploadUrl = async (body: DocumentUploadUrlRequest) => {
  return fastApi.post(`${PATH}/upload-url`, { json: body }).json<DocumentUploadUrlResponse>();
};

// The PUT goes straight to the vault, so it carries no bearer token and must send the signed headers as given
export const uploadDocument = async ({ file, source }: { file: File; source: DocumentSource }) => {
  const filename = toDocumentFilename(file.name);
  const contentType = DOCUMENT_FILE_TYPES_BY_SOURCE[source][getDocumentExtension(filename)];

  if (!contentType) throw new Error(`Unsupported file type for ${source}`);

  const { documentId, url, headers } = await postDocumentUploadUrl({ filename, contentType, sizeBytes: file.size, source });
  const response = await fetch(toDocumentUploadRequestUrl(url), { method: 'PUT', headers, body: file });

  if (!response.ok) throw new Error(`Document upload failed (${response.status})`);

  return documentId;
};
